import { useEffect, useCallback, useState, ReactNode, DetailedHTMLProps, HTMLAttributes } from 'react';
import * as runtime from 'react/jsx-runtime';
import { evaluate } from '@mdx-js/mdx';
import { MDXModule } from 'mdx/types';
import { MDXProvider } from '@mdx-js/react';
import { Article } from '@api/types/Article';
import { useGetArticleQuery } from '@api/baseApi';
import { LoadingDiv } from '@features/mdx/loading-div/LoadingDiv';
import { ErrorDiv } from '@features/mdx/error-div/ErrorDiv';
import { components } from '@features/mdx/MdxComponents';
import { MdxPageArticleIds } from '@features/mdx/MdxPageArticleIds';
import { getCurrentLanguageCode } from '@translations/functions';



type MdxDivProps = Readonly<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>> & {
	articleIds: MdxPageArticleIds;
	childrenBeforeMd?: ReactNode;
	children?: ReactNode;
}


export const MdxDiv = (props: MdxDivProps) => {
	const { articleIds, className, childrenBeforeMd, children, ...otherProps } = props;

	const divClassName = `md-page${className ? ` ${className}` : ''}`;

	const languageCode = getCurrentLanguageCode();
	const articleId = articleIds[languageCode] ? articleIds[languageCode] : '';
	const { data, isLoading, isFetching, isError, isSuccess } = useGetArticleQuery({ id: articleId });

	const [ content, setContent ] = useState<MDXModule | null>(null);
	const [ loadContent, setLoadContent ] = useState<boolean>(false);
	
	
	useEffect(() => {
		if (data) loadMdx(data);
	}, [ data ]);

	
	const loadMdx = useCallback(async(data: Article) => {
		setLoadContent(true);
		const parsedComponents = await evaluate(data.content, {
			...runtime,
			useMDXComponents: () => components
		});
		if (parsedComponents) {
			setContent(parsedComponents);
		}
		setLoadContent(false);
	}, []);


	if (isLoading || isFetching || loadContent) return (
		<div {...otherProps} className={divClassName}>
			<LoadingDiv />
		</div>
	)

	if (isError || (isSuccess && !content)) return (
		<div {...otherProps} className={divClassName}>
			<ErrorDiv />
		</div>
	)

	return (
		<MDXProvider components={components}>
			<div {...otherProps} className={divClassName}>
			
				{childrenBeforeMd &&
					<>{childrenBeforeMd}</>}
				
				{content?.default({})}
				
				{children &&
					<>{children}</>}
		
			</div>
		</MDXProvider>
	);
};